import { useEffect, useState, useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import CommonLoadingIndicator from "./CommonLoadingIndicator";
import { Symptom } from "../StudyDash/types";
import { formatUTCDate, formatUTCTime, getTimezoneAbbreviation } from "../../../../utils/helperFunctions";
import { useTimezone } from "../../../../utils/TimezoneContext";

type TableProps = {
  data: Array<Symptom>;
};

// Define a type for severity levels
type SeverityLevel = 1 | 2 | 3 | 4 | 5;

// Define a dictionary type
const severityDictionary: Record<SeverityLevel, string> = {
  1: "Very Low",
  2: "Low",
  3: "Moderate",
  4: "High",
  5: "Very High",
};

// Define a type for general feeling levels
type GeneralFeelingLevel = 1 | 2 | 3 | 4 | 5;

// Define a dictionary for general feeling
const generalFeelingDictionary: Record<GeneralFeelingLevel, string> = {
  1: "Very Bad",
  2: "Bad",
  3: "Neutral",
  4: "Good",
  5: "Very Good",
};

function getGeneralFeelingLabel(feeling: GeneralFeelingLevel): string {
  return generalFeelingDictionary[feeling];
}

// Function to map severity value to label
export function getSeverityLabel(severity: SeverityLevel): string {
  return severityDictionary[severity];
}

export function transformDataToRows(
  data: Array<Symptom>,
  allColumns: boolean = false,
  timezone?: string
) {
  return data.map((value: any) => {
    return {
      ...(allColumns ? value : {}),
      id: value.$id,
      symptomDate: formatUTCDate(value.symptomTime, timezone),
      symptomTime: formatUTCTime(value.symptomTime, timezone),
      rawDateTime: value.symptomTime || "",
      severity: getSeverityLabel(value.severity),
      name: value.name,
      generalFeeling: getGeneralFeelingLabel(value.generalFeeling.toString()),
      tags: value.tags,
      symptomCategory: value.symptomCategory,
      location: value.location,
    };
  });
}

function SymptompsTable({ data }: TableProps) {
  const { timezone } = useTimezone();
  const [tableRows, setTableRows] = useState<Array<Object>>();
  const [dataLoaded] = useState<Boolean>(true);

  const tzAbbr = getTimezoneAbbreviation(timezone, true);
  const dateSortComparator = (_v1: string, _v2: string, params1: any, params2: any) => {
    const raw1 = params1.api.getRow(params1.id)?.rawDateTime || "";
    const raw2 = params2.api.getRow(params2.id)?.rawDateTime || "";
    return raw1.localeCompare(raw2);
  };
  const columns = useMemo(
    () => [
      { field: "name", headerName: "Name", width: 200 },
      { field: "symptomCategory", headerName: "Symptom Category", width: 200 },
      { field: "symptomDate", headerName: "Date (" + tzAbbr + ")", width: 150, sortComparator: dateSortComparator },
      { field: "symptomTime", headerName: "Time (" + tzAbbr + ")", width: 150, sortComparator: dateSortComparator },
      { field: "severity", headerName: "Severity", width: 200 },
      { field: "generalFeeling", headerName: "General Feeling", width: 200 },
      { field: "tags", headerName: "Tags", width: 200 },
      { field: "location", headerName: "Location", width: 200 },
      { field: "userIdInt", headerName: "User ID", width: 200 },
      { field: "additionalNotes", headerName: "Additional Notes", width: 200 },
    ],
    [tzAbbr]
  );

  useEffect(() => {
    if (!data) return;
    try {
      const transformedRows = transformDataToRows(data, false, timezone);
      setTableRows(transformedRows);
    } catch (error) {
      console.log(error);
    }
  }, [data, timezone]);

  if (tableRows && dataLoaded) {
    return (
      <DataGrid
        data-testid="symptoms-table"
        rows={tableRows}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        checkboxSelection
        disableRowSelectionOnClick
        pageSizeOptions={[5, 10, 20]}
      />
    );
  } else {
    return <CommonLoadingIndicator />;
  }
}

export default SymptompsTable;
